import type { Data } from '@repo/strapi-types'
import AppLink from '@/components/elementary/AppLink'
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import { BasicImage } from '@/components/page-builder/components/BasicImage'
import { cn } from '@/lib/styles'
import type { ServerRenderedEntry } from '@/types/helpers'
import { getColorVariant } from '@/utils/colorMapping'
import { getHeightValue } from '@/utils/heightMapping'
import { getPositionValue } from '@/utils/positionMapping'

export function BannerCard({
	card,
	cardBackgroundColorVariant,
	borderColorVariant,
	customTitleGap,
	customImageGap,
}: {
	readonly card: ServerRenderedEntry<Data.Component<'shared.banner-card'>>
	readonly cardBackgroundColorVariant?: string | null
	readonly borderColorVariant?: string | null
	readonly customTitleGap?: string
	readonly customImageGap?: string
}) {
	return (
		<div
			className={cn(
				'flex flex-col relative p-3 lg:p-4 lg:p-4 gap-5 overflow-hidden group rounded-md flex-1 border border-transparent',
				cardBackgroundColorVariant,
				borderColorVariant,
				customImageGap,
			)}
		>
			{card.image && (
				<div className={cn('w-full relative', getHeightValue(card.imageHeight))}>
					<BasicImage
						fill
						component={card.image}
						className={cn(
							'object-contain',
							getPositionValue(card.imagePosition?.position, 'object'),
						)}
					/>
				</div>
			)}

			<div className={cn('flex flex-col gap-3 lg:gap-5', customTitleGap)}>
				{card.serverProcessedHtmlTitle ? (
					card.serverProcessedHtmlTitle
				) : (
					<CkEditorCSRRenderer
						variant="textOnly"
						htmlContent={card.title}
						defaultTypography="headingMedium"
					/>
				)}

				{card.serverProcessedHtmlDescription ? (
					card.serverProcessedHtmlDescription
				) : (
					<CkEditorCSRRenderer
						variant="textOnly"
						defaultTypography="bodyMedium"
						htmlContent={card.description}
					/>
				)}
			</div>
			{card.hoverAnimation && (
				<AppLink
					href={card.hoverAnimation.link?.href ?? '#'}
					openExternalInNewTab={card.hoverAnimation.link?.newTab ?? true}
					className={cn(
						'absolute w-full hover:text-current active:text-current group-hover:translate-y-0 transition-transform duration-300 ease-in-out h-full left-0 top-0 flex flex-col gap-3 rounded-md p-5 lg:translate-y-full opacity-0 lg:opacity-100',
						getColorVariant(card.hoverAnimation?.backgroundColor?.color, 'bg', 'neutral-light-500'),
					)}
				>
					<div className="h-full max-h-full overflow-y-auto">
						{card?.serverProcessedHtmlContent ? (
							card.serverProcessedHtmlContent
						) : (
							<CkEditorCSRRenderer
								variant="textOnly"
								defaultTypography="bodyMedium"
								htmlContent={card.hoverAnimation?.description}
							/>
						)}
					</div>
					{card.hoverAnimation?.icon && (
						<div className={cn('w-full relative h-8')}>
							<BasicImage fill component={card.hoverAnimation?.icon} className="object-contain" />
						</div>
					)}
				</AppLink>
			)}
		</div>
	)
}
